import { ChartContainer, cn, Recharts, type ChartConfig } from 'ui';

const chartConfig = {
  progress: {
    label: 'Progress',
    color: 'var(--chart-5)',
  },
} satisfies ChartConfig;

export type ProgressCircleProps = {
  value: number;
  children?: React.ReactNode;
  className?: string;
};

export function ProgressCircle({
  value,
  children,
  className,
}: ProgressCircleProps) {
  // Ensure value is between 0 and 100
  const normalizedValue = Math.min(Math.max(value, 0), 100);
  const startAngle = 90; // Start from top (90 degrees)
  const endAngle = startAngle - (normalizedValue / 100) * 360;
  const chartData = [{ name: 'progress', value: normalizedValue }];

  return (
    <ChartContainer
      config={chartConfig}
      className={cn('aspect-square size-[104px]', className)}
    >
      <Recharts.ResponsiveContainer width="100%" height="100%">
        <Recharts.RadialBarChart
          data={chartData}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={45}
          outerRadius={65}
        >
          <Recharts.PolarGrid
            gridType="circle"
            radialLines={false}
            stroke="none"
            className="first:fill-success-100 last:fill-white"
            polarRadius={[50, 40]}
          />
          <Recharts.RadialBar
            dataKey="value"
            background
            cornerRadius={10}
            fill="var(--chart-5)"
          />
          <Recharts.PolarRadiusAxis
            type="number"
            domain={[0, 100]}
            tick={false}
            tickLine={false}
            axisLine={false}
          >
            <Recharts.Label
              content={({ viewBox }) => {
                if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="fill-foreground"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={viewBox.cy}
                        className="text-xl font-semibold text-primary"
                      >
                        {children || normalizedValue}
                      </tspan>
                    </text>
                  );
                }
              }}
            />
          </Recharts.PolarRadiusAxis>
        </Recharts.RadialBarChart>
      </Recharts.ResponsiveContainer>
    </ChartContainer>
  );
}
